import React, { useEffect, useRef, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import PageHeader from "../../components/global-layout/page-header";
import { MonitorIcon } from "../../components/svg";
import { useTranslation } from "react-i18next";
import PageLoader from "../../components/UI/page-loader";
import Authentication from "../../auth/Authentication";
import { useMyContext } from "../../components/my-context/MyContext";
import { useLoadMore, convertUTCToLocal } from "../../utils/common";
import GridToolBar from "../../components/UI/grid-toolbar";
import { remoteMonitoringServices } from "../../services/InspectionOneServices";
import ScreenshotModal from "../../components/UI/screen-shot-modal/screen-shot-modal";
import RemoteMonitoringCard from "../../components/UI/remote-monitor-card/RemoteMonitoringCard";
import DeleteUser from "../../components/section/user-management/delete-user";
import NoDataFound from "../../components/UI/no-data-found";
import { ToastError } from "../../components/UI/toast";
import ToastifyMessage from "../../lib/toastify";
// --- Interfaces ---
interface Option {
  id: number;
  value: string;
  label: string;
}

// --- Constants ---
const durationOptions: Option[] = [
  { id: 1, value: "noFilter", label: "All time" },
  { id: 2, value: "today", label: "Today" },
  { id: 3, value: "thisWeek", label: "This Week" },
  { id: 4, value: "thisMonth", label: "This Month" },
  { id: 5, value: "thisYear", label: "This Year" },
  { id: 6, value: "custom", label: "Custom" },
];

function RemoteMonitoring({ userUpdated, setUserUpdated }: any) {
  const { isExternalUser, setUserRole } = useMyContext();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { pathname } = useLocation();
  // --- State ---
  const [startIndex, setStartIndex] = useState(0);
  const [listData, setListData] = useState<any>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSkeletonLoader, setIsSkeletonLoader] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [limit] = useState(20);
  const [initialLoading, setIntialLoading] = useState(true);
  const [finalFetch, setFinalFetch] = useState(false);
  const scrollGroundRef = useRef<any>(null);
  const [sorting, setSorting] = useState<number>(0);
  const [searchedInputVal, setSearchedInputVal] = useState("");
  const [sortingColumnName, setSortingColumnName] = useState<string>("");
  const [pageLoader, setPageLoader] = useState(false);
  const [isGridView, setIsGridView] = useState(false);
  const [toolItems, setToolItems] = useState<any>([]);
  const [searchedQuery, setSearchedQuery] = useState<any>(undefined);
  const [isDeleted, setIsDeleted] = useState<boolean>(false);
  const [isUpdated, setIsUpdated] = useState<boolean>(false);
  const [resetSearch, setResetSearch] = useState<any>(false);
  const [isRefreshed, setIsRefreshed] = useState<any>(false);
  const [noDataFound, setNoDataFound] = useState<boolean>(false);
  const [isFetchMore, setIsFetchMore] = useState<any>(false);
  const { pageTitle } = useMyContext();
  const authService = Authentication();
  const [selectedOption, setSelectedOption] = useState<Option>(durationOptions[0]);
  const [startDate, setStartDate] = useState<string | null>(null);
  const [endDate, setEndDate] = useState<string | null>(null);
  const [activeMenuId, setActiveMenuId] = useState<string | null>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [showScreenshotModal, setShowScreenshotModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState<any>(null);
  const { ToastifyHandle } = ToastifyMessage();

  // #region Use Effect
  useEffect(() => {
    const tools = ["refresh", "remoteMonitoring"];
    setToolItems(tools);
  }, []);

  // useEffect(() => {
  //   if (searchedQuery !== undefined) {
  //     setSearchedInputVal(searchedQuery);
  //   }
  // }, [searchedQuery]);

  useEffect(() => {
    if (selectedOption.id === 6 && (!startDate || !endDate)) {
      return;
    }
    if (!pathname.includes("/remote-monitoring-view")) {
      setStartIndex(0);
      listFetch([]);
      setUserUpdated(undefined);
      setResetSearch(false);
    }
  }, [sortingColumnName, sorting, isDeleted, isUpdated, isRefreshed, selectedOption, startDate, endDate, pathname]);

  // #endregion

  const handleError = (titleName: any, message: any) => {
    ToastifyHandle(
      <ToastError title={titleName} description={message} />,
      "error"
    );
  };

  // #region API Fetch
  const listFetch = async (query: any) => {
    setIsLoading(true);
    setIsSkeletonLoader(true);
    setFinalFetch(false);
    setNoDataFound(false);

    try {
      // Get Current User ID

      const userId = sessionStorage.getItem("userId");

      // Prepare Request
      // Note: Adding pagination/search params here if your API supports them, 
      // otherwise passing just the ID as requested.
      const requestData = {
        sourceUserId: userId,
        // searchText: query, // Uncomment if API supports search
        // startIndex: startIndex,
        // limit: limit
      };
      const response = await remoteMonitoringServices.RemoteMonitoringCapturesFetchService(requestData);
      if (response?.success == true) {
        const resultData = response.data || [];

        // Map Response
        const mappedData = resultData.map((item: any) => {
          const localDateTime = convertUTCToLocal(item.createdAt);
          const dateParts = localDateTime !== "-" ? localDateTime.split(',') : ["", ""];

          return {
            ...item,
            date: dateParts[0] || "00-00-0000",
            time: dateParts[1] || "--:--",
          };
        });

        // Handle Pagination (Append or Replace)
        if (startIndex === 0) {
          setListData(mappedData);
        } else {
          setListData((prev: any) => [...prev, ...mappedData]);
        }

        setTotalCount(response.data?.totalCount || mappedData.length);

        if (mappedData.length === 0 && startIndex === 0) {
          setNoDataFound(true);
        } else {
          setNoDataFound(false);
        }
      } else {
        // Handle non-200 response
        if (startIndex === 0) setListData([]);
        setNoDataFound(true);
        setIsLoading(false);
        handleError(t("Gallery"), response.message || "Failed to fetch gallery data.");
      }

    } catch (error) {
      console.error("Gallery Fetch Error:", error);
      if (startIndex === 0) setListData([]);
      handleError("Connection Error", error);
    } finally {
      setIsLoading(false);
      setIsSkeletonLoader(false);
      if (startIndex < limit) {
        setIntialLoading(false);
      }
    }
  };
  // #endregion

  // #region Date Handlers
  const handleDateClearAndFetch = () => {
    handleOptionChange(durationOptions[0]);
  };

  const handleOptionChange = (option: Option | null) => {
    if (!option) return;
    if (option.id === 6 && startDate && endDate) {
      const formattedLabel = `${startDate} - ${endDate}`;
      const customOption = { ...option, label: formattedLabel };
      setSelectedOption(customOption);
    } else {
      setSelectedOption(option);
    }
    if (option.id !== 6) {
      setStartDate(null);
      setEndDate(null);
    }
    setStartIndex(0);
  };
  // #endregion

  // #region Handlers
  const handleRemoteMonitoringClick = () => {
    setListData([]);
    navigate("remote-monitoring-view");
  };

  const handleRefreshFn = () => {
    setListData([]);
    setIsRefreshed(!isRefreshed);
  };
  const handleDeleteItemYes = async () => {
    setPageLoader(true);
    try {
      const userId = sessionStorage.getItem("userId");
      const requestPayload = {
        ...selectedItem,
        actionMode: 2,
      };
      const response = await remoteMonitoringServices.RemoteMonitoringCapturesSaveService(requestPayload);

      if (response?.success == true) {
        // Remove from local list
        setListData((prev: any) => prev.filter((d: any) => d.uniqueId !== selectedItem.uniqueId));
      } else {
        setIsLoading(false);
        handleError(t("Gallery"), response.message || "Delete Failed.");
      }
    } catch (err) {
      console.error("Delete Error", err);
      handleError("Connection Error", err);
    } finally {
      setPageLoader(false);
      setShowDeleteModal(false);
    }
  };
  const handleDeleteItem = (item: any) => {
    setSelectedItem(item);
    setShowDeleteModal(true);
  };

  const handleEditItem = (item: any) => {
    setSelectedItem(item);
    setShowScreenshotModal(true);
  };

  const handleDownloadItem = (item: any) => {
    if (item.image) {
      const link = document.createElement('a');
      link.href = item.image;
      link.download = `capture_${item.id}.jpg`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };
  const handleMoreOptions = (e: any, id: string) => {
    e.stopPropagation();
    // Toggle: if clicking same, close it. if clicking new, open it.
    setActiveMenuId(activeMenuId == id ? null : id);
    console.log("More Options Clicked for ID:", id);
  };

  // --- Menu Action Handler ---
  const handleOptionSelect = (option: any, item: any) => {
    if (option.label === "Download") {
      // Handle Download logic
      if (item.image) {
        const link = document.createElement('a');
        link.href = item.image;
        link.download = `capture_${item.id}.jpg`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } else if (option.label === "Delete") {
      // Handle Delete Logic
      // Call Delete API here if available, then refresh list
      console.log("Delete item:", item.id);
    } else if (option.label == "Edit") {
      console.log("Edit item:", item.id);
      setSelectedItem(item);
      setShowScreenshotModal(true);
    }
    setActiveMenuId(null);
  };

  const handleSaveScreenshot = async (description: string) => {
    if (!selectedItem) return;
    const userId = sessionStorage.getItem("userId");
    const userName: any = authService.decryptData("UserDetails", 2);;

    setPageLoader(true);

    try {
      const requestPayload = {
        ...selectedItem,
        sourceUserId: userId,
        sourceUserName: (userName?.firstName + " " + userName?.lastName).trim(),
        description: description,
        actionMode: 1,
      };

      const saveResponse = await remoteMonitoringServices.RemoteMonitoringCapturesSaveService(requestPayload);

      if (saveResponse?.success == true) {
        // Update local list to reflect changes immediately
        const updatedList = listData.map((item: any) =>
          item.uniqueId === selectedItem.uniqueId
            ? { ...item, description: description }
            : item
        );
        setListData(updatedList);

        // Close Modal
        handleCloseModal();
      } else {
        setIsLoading(false);
        handleError(t("Gallery"), saveResponse?.data?.responseMessage|| "Failed to Update gallery data.");
      }
    } catch (error) {
      console.error("Update API Error:", error);
      handleError("Connection Error", error);
    } finally {
      setPageLoader(false);
    }
  };

  const handleCloseModal = () => {
    setShowScreenshotModal(false);
    setSelectedItem(null);
  };
  // #endregion

  // #region Scroll
  const { handleScroll } = useLoadMore(
    startIndex,
    limit,
    totalCount,
    setStartIndex,
    setIsFetchMore,
    setFinalFetch,
    scrollGroundRef
  );
  // #endregion

  return (
    <>
      {!pathname.includes("/remote-monitoring/remote-monitoring-view") && (
        <div className="w-full h-full flex flex-col">
          <PageHeader
            title={'Remote Monitoring'}
            icon={<MonitorIcon />}
          />
          <div className="w-full flex-1 overflow-hidden">
            <div className="w-full h-full flex flex-col p-4">
              <div>
                <h2 className="text-[3rem] text-io-black font-inter font-semibold mb-6 dark:text-io-white">
                  {t("Gallery")}
                </h2>
              </div>

              {/* --- Grid Tool Bar --- */}
              <div className="mb-4">
                <GridToolBar
                  columnCount={listData.length}
                  gridType={t("Images")}
                  selectView={false}
                  tools={toolItems}
                  handleAddRow={handleRemoteMonitoringClick}
                  // filterComponent={
                  //   <div className="w-[25rem]">
                  //     <SelectDuration
                  //       options={durationOptions}
                  //       labelName="Duration"
                  //       getOptionLabel={(option: any) => option.label}
                  //       getOptionValue={(option: any) => option.value}
                  //       isReset={false}
                  //       isDisabled={isSkeletonLoader}
                  //       handleChange={handleOptionChange}
                  //       defaultValue={selectedOption}
                  //       setStartDate={setStartDate}
                  //       setEndDate={setEndDate}
                  //       onClose={handleDateClearAndFetch}
                  //     />
                  //   </div>
                  // }
                  handleRefresh={handleRefreshFn}
                  handleDelete={() => console.log("Delete")}
                  handleColumns={() => console.log("Cols")}
                  isGridView={isGridView}
                  fetchData={(query: any) => setSearchedQuery(query)}
                  getSearchedQuery={(query: any) => setSearchedQuery(query)}
                  handleExcel={() => console.log("Excel")}
                  isExcelLoading={false}
                  resetSearch={resetSearch}
                  isSkeletonLoader={isSkeletonLoader}
                  setIsGridView={(isChecked: any) => setIsGridView(isChecked)}
                />
              </div>

              {/* --- Gallery Grid Section --- */}
              <div
                ref={scrollGroundRef}
                onScroll={handleScroll}
                className="flex-1 overflow-y-auto pr-2"
              >
                {listData.length > 0 ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                    {listData.map((item: any, index: number) => (
                      <RemoteMonitoringCard
                        key={item.uniqueId || index}
                        item={item}
                        isExternalUser={isExternalUser}
                        onEdit={handleEditItem}
                        onDelete={handleDeleteItem}
                        onDownload={handleDownloadItem}
                      />
                    ))}
                  </div>
                ) : (
                  !isLoading && (
                    <div className="w-full h-full flex items-center justify-center text-gray-500">
                      <NoDataFound />
                    </div>
                  )
                )}

                {(isLoading || isSkeletonLoader) && (
                  <div className="w-full py-4 text-center text-gray-500">
                    <PageLoader />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {showScreenshotModal && selectedItem && (
        <ScreenshotModal
          imageSrc={selectedItem.url}
          isLoading={pageLoader}
          initialDescription={selectedItem.description}
          onClose={handleCloseModal}
          onSave={handleSaveScreenshot}
        />
      )}
      {showDeleteModal && selectedItem && (
        <DeleteUser
          rowData={selectedItem}
          handleCancel={() => setShowDeleteModal(false)}
          handleDelete={(rowData: any) => handleDeleteItemYes()}
          actionFlag="common"
        />
      )}
      <Outlet />
    </>
  );
}

export default RemoteMonitoring;